import React from 'react';
import { DroppableTaskColumn } from './DroppableTaskColumn';
import { useAvailableTags } from '../../../../hooks/queries/useTasks';
import { useTaskColumns } from '../../../../hooks/queries/useTaskColumns';
import { TaskItem } from '../../../../types/topic';
import { FormattedTaskColumn } from '../../../../hooks/queries/taskColumnTypes';
import { UserFilter } from '../../../dashboard/types/dashboardTypes';

interface DroppableTaskColumnContainerProps {
  column: FormattedTaskColumn;
  items: TaskItem[];
  projectId: string;
  topicId: string;
  isRecording: boolean;
  isCreating: boolean;
  activeColumn: TaskItem['status'];
  title: string;
  description: string;
  tags: string[];
  deadline: Date | null;
  showDescription: boolean;
  inputRef: React.RefObject<HTMLInputElement>;
  itemToDelete: string | null;
  totalTaskCount: number;
  isEditingColumnName: string | null;
  isEditingColumnColor: string | null;
  className?: string;
  activeTask?: string | null;
  hasCollaborators?: boolean;
  showUnscheduledOnly?: boolean;
  showUnassignedOnly?: boolean;
  userFilters?: UserFilter[];
  onVoiceClick: () => void;
  onAddClick: () => void;
  onDelete: (id: string) => void;
  onDeleteClick: (id: string) => void;
  onCancelDelete: () => void;
  onUpdateTask: (id: string, updates: Partial<TaskItem>) => void;
  onTitleChange: (value: string) => void;
  onDescriptionChange: (value: string) => void;
  onTagsChange: (value: string[]) => void;
  onDeadlineChange: (value: Date | null) => void;
  onAssigneeChange?: (value: string | null) => void;
  onSubmit: () => void;
  onCancel: () => void;
  moveTask: (id: string, toIndex: number) => void;
  moveTaskBetweenColumns: (id: string, newStatus: TaskItem['status'], newIndex: number) => void;
  onEditColumnNameClick: (columnId: string) => void;
  onEditColumnName: (columnId: string, name: string) => void;
  onDeleteColumnClick: (columnId: string) => void;
  onColumnColorSelect: (columnId: string, color: string) => void;
  onAddColumnAfter: (columnId: string) => void;
  onMoveColumnLeft: (columnId: string) => void;
  onMoveColumnRight: (columnId: string) => void;
  forceTaskMove?: (taskId: string, targetColumnStatus: string) => void;
}

/**
 * Container component that handles business logic for DroppableTaskColumn
 * Fetches data and processes it before passing to presentational component
 */
export const DroppableTaskColumnContainer: React.FC<DroppableTaskColumnContainerProps> = props => {
  // Fetch business data
  const availableTags = useAvailableTags(props.topicId);
  const {
    data: columns = [],
    isLoading: isColumnsLoading,
    isError: isColumnsError,
  } = useTaskColumns(props.topicId);

  // Process data for presentation
  const sortedItems = [...props.items].sort((a, b) => (a.position || 0) - (b.position || 0));
  const taskIds = sortedItems.map(task => task.id);

  // Handle loading and error states
  if (isColumnsLoading) {
    return (
      <div className="flex flex-col h-full relative z-10 p-4">
        <div className="flex items-center justify-center h-32">
          <div className="animate-spin h-6 w-6 border-2 border-gray-300 border-t-blue-600 rounded-full"></div>
        </div>
      </div>
    );
  }

  if (isColumnsError) {
    return (
      <div className="flex flex-col h-full relative z-10 p-4">
        <div className="flex items-center justify-center h-32 text-red-500">
          Failed to load column data
        </div>
      </div>
    );
  }

  // Pass processed data to presentational component
  return (
    <DroppableTaskColumn
      {...props}
      items={sortedItems}
      availableTags={availableTags}
      columns={columns}
      taskIds={taskIds}
    />
  );
};
